"use client"

import { useEffect, useRef, useState } from "react"

import { LoadingTextCarousel } from "@/components/flow/LoadingTextCarousel"
import { CHECKOUT_PROGRESS } from "@/components/flow/progress"
import { StepWrapper } from "@/components/flow/StepWrapper"
import { ErrorRenderer } from "@/features/checkout/components/ErrorRenderer"
import { mapErrorToMessage } from "@/platform/shopper/carts.client"

interface WaitingRoomStepProps {
  cartError?: Error | null
  testId?: string
  progressPercentage?: number
  onCloseVerification?: () => void
  onLeave?: () => void
  verificationRecovery?: { kind: "recapture" | "status"; onRecover: () => Promise<unknown> | void }
}

export const WaitingRoomStep = ({
  cartError,
  testId = "waiting-room",
  progressPercentage = CHECKOUT_PROGRESS.UNDERWRITING,
  onCloseVerification,
  onLeave,
  verificationRecovery,
}: WaitingRoomStepProps) => {
  const failed = !!verificationRecovery
  const leavable = !!cartError && !!onLeave
  const recover = verificationRecovery?.onRecover
  const [recovering, setRecovering] = useState(false)
  const inFlight = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const recovery = recover
    ? () => {
        if (inFlight.current) return
        const pending: unknown = recover()
        if (!(pending instanceof Promise)) return
        inFlight.current = true
        setRecovering(true)
        void pending
          .catch(() => {})
          .finally(() => {
            inFlight.current = false
            if (mounted.current) setRecovering(false)
          })
      }
    : undefined
  return (
    <div data-testid={testId}>
      <StepWrapper
        showBackButton={failed ? !!onCloseVerification : leavable}
        onBack={failed ? onCloseVerification : onLeave}
        title={failed ? "No pudimos completar tu verificación" : "Gracias por tu paciencia..."}
        subtitle={
          failed
            ? verificationRecovery.kind === "recapture"
              ? "Revisa tu documento y vuelve a tomar las fotos."
              : "No pudimos consultar el resultado. Tus fotos ya se enviaron."
            : "Estamos procesando tu información. Esto podría tardar unos minutos."
        }
        progressPercentage={progressPercentage}
        hideSubmitButton={!failed}
        isLoading={recovering}
        submitDisabled={!recovery || recovering}
        onSubmit={recovery}
        submitButtonText={verificationRecovery?.kind === "status" ? "Consultar resultado" : "Volver a verificar"}
      >
        <div className="space-y-8">
          {leavable ? (
            <ErrorRenderer error="No pudimos cargar tu carrito. Seguimos intentando; también puedes volver." title="Error al cargar el carrito" />
          ) : (
            cartError && <ErrorRenderer error={mapErrorToMessage(cartError)} title="Error al cargar el carrito" dismissible />
          )}
          {!failed && <LoadingTextCarousel cycleDuration={6000} className="py-4" />}
          {!failed && !leavable && (
            <div className="bg-nelo-sky-50 mt-3 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-nunito text-fg text-lg leading-tight">Por favor no presiones atrás ni actualices la página durante estos momentos.</h4>
                </div>
              </div>
            </div>
          )}
        </div>
      </StepWrapper>
    </div>
  )
}
